import { Link, Outlet, createBrowserHistory, createMemoryHistory, createRootRoute, createRoute, createRouter } from '@tanstack/react-router';
import { industries, posts, getPost, getIndustry, postsByIndustry, site } from './data';
import { articleSchema, faqSchema, pageTitle, websiteSchema } from './seo';

function JsonLd({ data }: { data: object }) {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />;
}

function Layout() {
  return (
    <>
      <header className="topbar">
        <Link to="/" className="brand" aria-label="Global Origin Guide home">
          Global Origin Guide
        </Link>
        <nav>
          <Link to="/blog">Blog</Link>
          <Link to="/industries/$industrySlug" params={{ industrySlug: 'ai' }}>
            Industries
          </Link>
          <Link to="/about">About</Link>
        </nav>
      </header>
      <main>
        <Outlet />
      </main>
      <footer>
        <strong>{site.name}</strong>
        <span>Source-led buying research for global products and services.</span>
      </footer>
    </>
  );
}

function HomePage() {
  const featured = posts[0];
  return (
    <>
      <title>{pageTitle()}</title>
      <meta name="description" content={site.description} />
      <JsonLd data={websiteSchema()} />
      <section className="hero">
        <div>
          <p className="eyebrow">Global buying research</p>
          <h1>Products and services not made in the USA, mapped by industry.</h1>
          <p>
            Compare global brands, manufacturing origins, and service jurisdictions across AI,
            solar panels, autos, phones, semiconductors, appliances, fashion, medical equipment,
            and software.
          </p>
          <div className="actions">
            <Link to="/blog" className="button">
              Read the guide
            </Link>
            <Link to="/industries/$industrySlug" params={{ industrySlug: 'solar' }} className="button secondary">
              Browse industries
            </Link>
          </div>
        </div>
        <aside className="answer-card">
          <span>Direct answer</span>
          <p>{featured.summary}</p>
        </aside>
      </section>
      <section className="section">
        <div className="section-heading">
          <p className="eyebrow">Industry hubs</p>
          <h2>Start with the supply chain, then choose the product.</h2>
        </div>
        <div className="industry-grid">
          {Object.entries(industries).map(([slug, industry]) => (
            <Link key={slug} to="/industries/$industrySlug" params={{ industrySlug: slug }} className="tile">
              <strong>{industry.name}</strong>
              <span>{industry.description}</span>
            </Link>
          ))}
        </div>
      </section>
      <section className="section band">
        <div className="section-heading">
          <p className="eyebrow">Latest research</p>
          <h2>AI-search-ready explainers with sources.</h2>
        </div>
        <PostGrid posts={posts.slice(0, 3)} />
      </section>
    </>
  );
}

function BlogPage() {
  return (
    <>
      <title>{pageTitle('Blog')}</title>
      <meta name="description" content="Guides to products and services not made in the USA by industry, country, brand, and supply chain." />
      <section className="page-head">
        <p className="eyebrow">Blog</p>
        <h1>Global product-origin research</h1>
        <p>Clear guides for buyers who need to understand where products are designed, assembled, sourced, and supported.</p>
      </section>
      <section className="section">
        <PostGrid posts={posts} />
      </section>
    </>
  );
}

function IndustryPage() {
  const { industrySlug } = industryRoute.useParams();
  const industry = getIndustry(industrySlug);
  const filtered = postsByIndustry(industrySlug);
  if (!industry) return <NotFound />;
  return (
    <>
      <title>{pageTitle(`${industry.name} Not Made in USA`)}</title>
      <meta name="description" content={`${industry.description} Guides and examples for non-US products and services.`} />
      <section className="page-head compact">
        <p className="eyebrow">Industry hub</p>
        <h1>{industry.name}</h1>
        <p>{industry.description}</p>
      </section>
      <section className="section">
        {filtered.length ? <PostGrid posts={filtered} /> : <p className="empty">Articles for this hub are planned in the editorial map.</p>}
      </section>
    </>
  );
}

function PostPage() {
  const { slug } = postRoute.useParams();
  const post = getPost(slug);
  if (!post) return <NotFound />;
  return (
    <>
      <title>{pageTitle(post.title)}</title>
      <meta name="description" content={post.dek} />
      <JsonLd data={articleSchema(post)} />
      <JsonLd data={faqSchema(post)} />
      <article className="article">
        <header>
          <p className="eyebrow">{industries[post.industry].name}</p>
          <h1>{post.title}</h1>
          <p className="dek">{post.dek}</p>
          <div className="meta">
            <span>Updated {post.updated}</span>
            <span>{post.readingTime}</span>
            <span>{post.countryFocus.join(', ')}</span>
          </div>
        </header>
        <section className="answer-block">
          <h2>Quick Answer</h2>
          <p>{post.summary}</p>
        </section>
        <section>
          <h2>Key Takeaways</h2>
          <ul className="takeaways">
            {post.takeaways.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
        {post.sections.map((section) => (
          <section key={section.heading}>
            <h2>{section.heading}</h2>
            {section.body.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </section>
        ))}
        <section>
          <h2>Frequently Asked Questions</h2>
          <div className="faq-list">
            {post.faqs.map((faq) => (
              <div key={faq.q} className="faq">
                <h3>{faq.q}</h3>
                <p>{faq.a}</p>
              </div>
            ))}
          </div>
        </section>
        <section>
          <h2>Sources</h2>
          <ul className="sources">
            {post.sources.map((source) => (
              <li key={source.url}>
                <a href={source.url}>{source.label}</a>
              </li>
            ))}
          </ul>
        </section>
      </article>
    </>
  );
}

function AboutPage() {
  return (
    <>
      <title>{pageTitle('About')}</title>
      <meta name="description" content="About Global Origin Guide and its method for researching products not made in the USA." />
      <section className="page-head">
        <p className="eyebrow">Method</p>
        <h1>Origin research for buyers who need nuance.</h1>
        <p>
          We separate brand headquarters, final assembly, key component sourcing, software jurisdiction,
          warranty support, and public documentation. That keeps the research useful across physical
          products and digital services.
        </p>
      </section>
      <section className="section text">
        <h2>Editorial roadmap</h2>
        <p>
          The site is built around searchable industry hubs and AI-extractable answer blocks. Future
          articles should add country-by-country buying guides, model-specific origin pages, tariff-aware
          procurement explainers, and comparison pages for buyers evaluating US and non-US options.
        </p>
      </section>
    </>
  );
}

function PostGrid({ posts }: { posts: typeof import('./data').posts }) {
  return (
    <div className="post-grid">
      {posts.map((post) => (
        <Link key={post.slug} to="/blog/$slug" params={{ slug: post.slug }} className="post-card">
          <span>{industries[post.industry].name}</span>
          <h3>{post.title}</h3>
          <p>{post.dek}</p>
          <small>{post.readingTime}</small>
        </Link>
      ))}
    </div>
  );
}

function NotFound() {
  return (
    <section className="page-head">
      <h1>Page not found</h1>
      <p>This guide may have moved. Start from the blog index.</p>
      <Link to="/blog" className="button">
        Go to blog
      </Link>
    </section>
  );
}

const rootRoute = createRootRoute({ component: Layout });
const indexRoute = createRoute({ getParentRoute: () => rootRoute, path: '/', component: HomePage });
const blogRoute = createRoute({ getParentRoute: () => rootRoute, path: '/blog', component: BlogPage });
const aboutRoute = createRoute({ getParentRoute: () => rootRoute, path: '/about', component: AboutPage });
const industryRoute = createRoute({ getParentRoute: () => rootRoute, path: '/industries/$industrySlug', component: IndustryPage });
const postRoute = createRoute({ getParentRoute: () => rootRoute, path: '/blog/$slug', component: PostPage });

const routeTree = rootRoute.addChildren([indexRoute, blogRoute, aboutRoute, industryRoute, postRoute]);

export function createAppRouter(history = typeof window === 'undefined' ? createMemoryHistory({ initialEntries: ['/'] }) : createBrowserHistory()) {
  return createRouter({
    routeTree,
    history,
    defaultPreload: 'intent',
    context: {}
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
